import { AfterViewInit, Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { timer } from 'rxjs';
import { filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ChatLink, ChatMessage } from './workflow-assistant.models';
import { WorkflowAssistantService } from './workflow-assistant.service';
import { Roles } from 'src/app/common/enumerations/enumerations';
import { WorkflowAssistantSuggestionsDialogComponent } from './workflow-assistant-suggestions-dialog.component';
import { SearchDialogComponent } from '../head-page/head-search/search-dialog/search-dialog.component';
import { WorkflowAssistantFeedbackDialogComponent } from './workflow-assistant-feedback-dialog.component';

@Component({
  selector: 'app-workflow-assistant',
  templateUrl: './workflow-assistant.component.html',
  styleUrls: ['./workflow-assistant.component.scss'],
})
export class WorkflowAssistantComponent extends BaseComponent implements AfterViewInit {

  @ViewChild('messageList') private messageList!: ElementRef<HTMLElement>;
  @ViewChild('inputField') private inputField!: ElementRef<HTMLInputElement>;
  @ViewChild('assistantFab') private assistantFab!: ElementRef<HTMLButtonElement>;

  isOpen = false;
  inputText = '';
  messages: ChatMessage[] = [];
  isThinking = false;
  pendingQuestions: PendingQuestion[] = [];
  visibleTableExport: VisibleTableExport | null = null;
  fabLeft = 0;
  fabTop = 0;
  private readonly FAB_SIZE = 56;
  private dragState: { pointerId: number; startX: number; startY: number; initialLeft: number; initialTop: number } | null = null;
  private suppressToggleOnClick = false;
  private wasDragged = false;
  private questionHistory: string[] = [];
  private historyIndex = -1;
  private historyDraft = '';

  readonly QUICK_ACTIONS = [
    'What needs my approval?',
    'Show active SCFs',
    'List open RSSWAs',
    'Help',
  ];

  constructor(
    protected override injector: Injector,
    private assistantService: WorkflowAssistantService,
    private router: Router,
  ) {
    super(injector);

   const viewportWidth = Math.max(window.innerWidth || 1200, 480);
   const viewportHeight = Math.max(window.innerHeight || 800, 420);
   this.fabLeft = Math.max(16, viewportWidth - this.FAB_SIZE - 24);
   this.fabTop = Math.max(16, viewportHeight - this.FAB_SIZE - 24);

   this.router.events.pipe(
     filter((event): event is NavigationEnd => event instanceof NavigationEnd),
     switchMap(() => {
       // Remove the previous route's offer immediately, then inspect the newly rendered view.
       this.visibleTableExport = null;
       return timer(300);
     }),
     takeUntil(this.destroy$),
   ).subscribe(() => this.refreshVisibleTableExport());
  }

  ngAfterViewInit(): void {
   this.positionFabSafely();
  }

  /**
   * Exposes CBorg service interruptions to the assistant template without duplicating availability state.
   */
  get cborgUnavailable$() {
    return this.assistantService.cborgUnavailable$;
  }

  /**
   * Opens or closes the assistant, checks CBorg availability, and refreshes page-aware actions when opened.
   */
  togglePanel(): void {
    if (this.suppressToggleOnClick) {
      this.suppressToggleOnClick = false;
      return;
    }

    this.isOpen = !this.isOpen;
    if (this.isOpen && this.messages.length === 0) {
      this.pushAssistant(
        `👋 Hi ${this.currentUser?.name?.split(' ')[0] ?? 'there'}! I'm your ASCC Workflow Assistant.\n\nAsk me about form statuses, pending approvals, or type <strong>help</strong> to see all I can do.`,
      );
    }
    if (this.isOpen) {
      this.assistantService.checkCBorgAvailability();
      // Refresh after the panel renders so only the underlying page tables are considered.
      setTimeout(() => {
        this.refreshVisibleTableExport();
        this.inputField?.nativeElement?.focus();
      }, 100);
    }
  }

  onFabPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialLeft: this.fabLeft,
      initialTop: this.fabTop,
    };
    this.wasDragged = false;
    this.suppressToggleOnClick = false;
    (event.target as HTMLElement)?.setPointerCapture?.(event.pointerId);
  }

  onFabPointerMove(event: PointerEvent): void {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    const dx = event.clientX - this.dragState.startX;
    const dy = event.clientY - this.dragState.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      this.wasDragged = true;
      this.suppressToggleOnClick = true;
    }

    if (!this.wasDragged) return;

    const nextLeft = this.dragState.initialLeft + dx;
    const nextTop = this.dragState.initialTop + dy;
    this.fabLeft = this.clamp(nextLeft, 12, Math.max(12, window.innerWidth - this.FAB_SIZE - 12));
    this.fabTop = this.clamp(nextTop, 12, Math.max(12, window.innerHeight - this.FAB_SIZE - 12));
  }

  onFabPointerUp(): void {
    const dragState = this.dragState;
    this.dragState = null;
    if (!dragState) return;

    if (!this.wasDragged) {
      this.suppressToggleOnClick = false;
      return;
    }

    this.positionFabSafely();
    setTimeout(() => {
      this.suppressToggleOnClick = false;
      this.wasDragged = false;
    });
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private positionFabSafely(): void {
    const candidates = [
      { left: this.fabLeft, top: this.fabTop },
      { left: Math.max(16, window.innerWidth - this.FAB_SIZE - 16), top: Math.max(16, window.innerHeight - this.FAB_SIZE - 16) },
      { left: 16, top: Math.max(16, window.innerHeight - this.FAB_SIZE - 16) },
      { left: Math.max(16, window.innerWidth - this.FAB_SIZE - 16), top: 16 },
      { left: 16, top: 16 },
    ];

    const safePosition = candidates.find(candidate => !this.overlapsButton(candidate.left, candidate.top));
    if (safePosition) {
      this.fabLeft = this.clamp(safePosition.left, 12, Math.max(12, window.innerWidth - this.FAB_SIZE - 12));
      this.fabTop = this.clamp(safePosition.top, 12, Math.max(12, window.innerHeight - this.FAB_SIZE - 12));
    }
  }

  private overlapsButton(left: number, top: number): boolean {
    const fabRect = {
      left,
      top,
      right: left + this.FAB_SIZE,
      bottom: top + this.FAB_SIZE,
    };

    const interactiveButtons = Array.from(document.querySelectorAll<HTMLElement>('button, a[href], [role="button"]'))
      .filter(element => !element.closest('.fab-btn') && !element.closest('.chat-panel'));

    return interactiveButtons.some(element => {
      const rect = element.getBoundingClientRect();
      return !(rect.right < fabRect.left || rect.left > fabRect.right || rect.bottom < fabRect.top || rect.top > fabRect.bottom);
    });
  }

  /**
   * Downloads the currently rendered rows of the largest visible page table as an Excel workbook.
   */
  async exportVisibleTable(): Promise<void> {
    this.refreshVisibleTableExport();
    if (!this.visibleTableExport) return;

    try {
      // Load the workbook code only when an export is requested to keep normal page startup light.
      const XLSX = await import('xlsx');
      const data = [this.visibleTableExport.headers, ...this.visibleTableExport.rows];
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      worksheet['!cols'] = this.visibleTableExport.headers.map((_header, columnIndex) => ({
        wch: Math.min(50, Math.max(12, ...data.map(row => `${row[columnIndex] ?? ''}`.length + 2))),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ASCC Export');
      XLSX.writeFile(workbook, this.getExportFileName());
    } catch {
      this.pushAssistant('⚠️ The visible table could not be exported. Please try again.', undefined, true);
    }
  }

  /**
   * Finds exportable tables outside the assistant and keeps the one containing the most visible rows.
   * Home intentionally suppresses contextual exports even when dashboard tables are present.
   */
  private refreshVisibleTableExport(): void {
    if (this.isHomeRoute()) {
      this.visibleTableExport = null;
      return;
    }

    const exports = Array.from(document.querySelectorAll<HTMLElement>('mat-table, table'))
      .filter(table => !table.closest('.chat-panel') && this.isElementVisible(table))
      .map(table => this.extractTableData(table))
      .filter((table): table is VisibleTableExport => !!table)
      .sort((left, right) => right.rows.length - left.rows.length);

    this.visibleTableExport = exports[0] ?? null;
  }

  /**
   * Identifies the dashboard route where table export should not be offered.
   */
  private isHomeRoute(): boolean {
    const routePath = this.router.url.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '').toLowerCase();
    return !routePath || routePath === 'home';
  }

  /**
   * Converts rendered Material or native table rows into a rectangular export matrix.
   * @param table Visible table element to inspect.
   */
  private extractTableData(table: HTMLElement): VisibleTableExport | null {
    const rows = Array.from(table.querySelectorAll<HTMLElement>('tr, mat-header-row, mat-row, [role="row"]'))
      .filter((row, index, allRows) => allRows.indexOf(row) === index && row.closest('table, mat-table') === table);
    const headerRows = rows
      .map(row => this.getRowCells(row))
      .filter(cells => cells.some(cell => cell.matches('th, mat-header-cell, [role="columnheader"]')));
    const headerCells = headerRows.sort((left, right) => right.filter(cell => this.getCellText(cell)).length - left.filter(cell => this.getCellText(cell)).length)[0];
    if (!headerCells?.length) return null;

    const headers = headerCells.map(cell => this.getCellText(cell));
    const rawDataRows = rows
      .map(row => this.getRowCells(row))
      .filter(cells => cells.length && !cells.some(cell => cell.matches('th, mat-header-cell, [role="columnheader"]')))
      .map(cells => cells.map(cell => this.getCellText(cell)));
    // Drop unlabeled and icon-only action columns after Material icon ligatures are removed.
    const includedColumns = headers
      .map((header, index) => header && rawDataRows.some(row => row[index]) ? index : -1)
      .filter(index => index >= 0);
    if (!includedColumns.length) return null;

    const dataRows = rawDataRows
      .map(row => includedColumns.map(index => row[index] ?? ''))
      .filter(row => row.some(value => value));
    if (!dataRows.length) return null;

    return {
      headers: includedColumns.map(index => headers[index]),
      rows: dataRows,
    };
  }

  /**
   * Returns direct data cells without accidentally including cells from nested tables.
   * @param row Rendered table row.
   */
  private getRowCells(row: HTMLElement): HTMLElement[] {
    return Array.from(row.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement && child.matches('th, td, mat-header-cell, mat-cell, [role="columnheader"], [role="cell"], [role="gridcell"]'));
  }

  /**
   * Normalizes rendered cell content for a compact spreadsheet value without Material icon ligatures.
   * @param cell Rendered table cell, when present.
   */
  private getCellText(cell?: HTMLElement): string {
    if (!cell) return '';

    // Material icons render from text ligatures that Excel cannot reproduce without the icon font.
    const exportCell = cell.cloneNode(true) as HTMLElement;
    exportCell.querySelectorAll('mat-icon, .mat-icon, .material-icons, .material-symbols-outlined, .material-symbols-rounded, .material-symbols-sharp')
      .forEach(icon => icon.remove());
    return exportCell.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  }

  /**
   * Checks whether a table participates in the current page layout.
   * @param element Candidate table element.
   */
  private isElementVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return element.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  }

  /**
   * Builds a route-specific filename that is safe for Windows and browser downloads.
   */
  private getExportFileName(): string {
    const routeName = this.router.url.split(/[/?#]/).filter(Boolean).pop() ?? 'Table';
    const safeRouteName = routeName.replace(/[^a-z0-9_-]+/gi, '_');
    const date = new Date().toISOString().slice(0, 10);
    return `ASCC_${safeRouteName}_${date}.xlsx`;
  }

  /**
   * Adds the current input to history and sends it immediately, or queues it when another response is in progress.
   */
  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text) return;

    // Preserve submitted wording so users can revisit and revise earlier questions.
    this.questionHistory.push(text);
    this.historyIndex = -1;
    this.historyDraft = '';
    const userMessage = this.pushUser(text);
    this.inputText = '';
    if (this.isThinking) {
      // Queue follow-up questions while the current assistant response is being resolved.
      this.pendingQuestions.push({ id: this.newId(), text, draftText: text, messageId: userMessage.id });
      return;
    }

    this.startAssistantRequest(text);
  }

  /**
   * Resolves one user question and continues with queued follow-up questions.
   * @param text Question to parse and answer.
   */
  private startAssistantRequest(text: string): void {
    this.isThinking = true;
    this.pushLoading();

    this.assistantService.resolveIntent(text).pipe(
      // Keep the typing indicator visible until this question's store dependencies finish loading.
      switchMap(intent => this.assistantService.waitForStoreReadiness(intent).pipe(
        switchMap(() => this.assistantService.processIntent(intent)),
      )),
      finalize(() => this.processNextQueuedQuestion()),
    ).subscribe({
      next: (partial) => {
        this.isThinking = false;
        this.pushAssistant(partial.content ?? '', partial.links, partial.isError, text);
        this.performAssistantAction(partial);
      },
      error: () => {
        this.isThinking = false;
        this.pushAssistant('⚠️ Something went wrong. Please try again.', undefined, true);
      },
    });
  }

  /**
   * Starts the next pending question in FIFO order.
   */
  private processNextQueuedQuestion(): void {
    const nextQuestion = this.pendingQuestions.shift();
    if (nextQuestion) this.startAssistantRequest(nextQuestion.text);
  }

  /**
   * Enables inline editing for a pending question.
   * @param question Queued question to edit.
   */
  editPendingQuestion(question: PendingQuestion): void {
    question.draftText = question.text;
    question.isEditing = true;
  }

  /**
   * Saves pending question edits and mirrors the updated text in the chat transcript.
   * @param question Queued question being edited.
   */
  savePendingQuestion(question: PendingQuestion): void {
    const text = question.draftText.trim();
    if (!text) return;

    question.text = text;
    question.draftText = text;
    question.isEditing = false;
    const message = this.messages.find(m => m.id == question.messageId);
    if (message) message.content = text;
  }

  /**
   * Cancels pending question edits without changing the queued text.
   * @param question Queued question being edited.
   */
  cancelPendingEdit(question: PendingQuestion): void {
    question.draftText = question.text;
    question.isEditing = false;
  }

  /**
   * Removes a pending question before it is processed.
   * @param question Queued question to remove.
   */
  deletePendingQuestion(question: PendingQuestion): void {
    this.pendingQuestions = this.pendingQuestions.filter(q => q.id != question.id);
    this.messages = this.messages.filter(m => m.id != question.messageId);
  }

  onQuickAction(text: string): void {
    this.inputText = text;
    this.sendMessage();
  }

  /**
   * Runs application-level actions only after their assistant response has been added to the transcript.
   * @param message Assistant result containing an optional trusted action.
   */
  private performAssistantAction(message: Partial<ChatMessage>): void {
    if (message.action === 'search') {
      this.isOpen = false;
      // Match the main header search action while supplying the assistant's requested filter.
      this.dialog.open(SearchDialogComponent, {
        minHeight: '50vh',
        width: '95vw',
        autoFocus: false,
        disableClose: true,
        data: { initialFilter: message.actionValue ?? '' },
      });
      return;
    }

    if (message.action === 'print') {
      this.isOpen = false;
      // Let Angular hide the assistant before the browser captures the printable view.
      setTimeout(() => window.print());
    }
  }

  /**
   * Shows suggestion management only to Super Administrators; the API repeats this authorization check.
   */
  get canManageSuggestions(): boolean {
    return !!this.hasRoles([Roles.SA]);
  }

  /**
   * Opens the SA workspace for submitting, reviewing, and exporting assistant question suggestions.
   */
  openSuggestions(): void {
    // Use the card-based dialog structure shared by the rest of ASCC.
    this.dialog.open(WorkflowAssistantSuggestionsDialogComponent, {
      width: '800px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
    });
  }

  /**
   * Opens an audited incorrect-answer report for a completed assistant response.
   * @param message Assistant response and its originating question.
   */
  reportIncorrectAnswer(message: ChatMessage): void {
    if (!message.sourceQuestion || message.feedbackSubmitted) return;

    // Keep question and answer immutable in the dialog so the report describes the visible exchange.
    this.dialog.open(WorkflowAssistantFeedbackDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 32px)',
      data: { question: message.sourceQuestion, assistantAnswer: message.content },
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(submitted => {
      if (submitted) message.feedbackSubmitted = true;
    });
  }

  /**
   * Submits permanent Help access without discarding a question already being drafted.
   */
  showHelp(): void {
    const draft = this.inputText;
    this.inputText = 'Help';
    this.sendMessage();
    this.inputText = draft;
    setTimeout(() => this.inputField?.nativeElement?.focus());
  }

  /**
   * Handles message submission and navigation through submitted-question history.
   * @param event Keyboard event raised by the assistant input.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const direction = event.key === 'ArrowUp' ? -1 : 1;
      if (!this.navigateQuestionHistory(direction)) return;

      event.preventDefault();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Moves backward or forward through submitted questions while preserving the current draft.
   * @param direction Use -1 for older questions and 1 for newer questions.
   * @returns True when history navigation handled the key press.
   */
  private navigateQuestionHistory(direction: -1 | 1): boolean {
    if (!this.questionHistory.length || (direction === 1 && this.historyIndex < 0)) return false;

    if (direction === -1) {
      if (this.historyIndex < 0) {
        // Save unfinished text before entering history so ArrowDown can restore it.
        this.historyDraft = this.inputText;
        this.historyIndex = this.questionHistory.length - 1;
      } else {
        this.historyIndex = Math.max(0, this.historyIndex - 1);
      }
      this.inputText = this.questionHistory[this.historyIndex];
    } else if (this.historyIndex < this.questionHistory.length - 1) {
      this.historyIndex++;
      this.inputText = this.questionHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.inputText = this.historyDraft;
      this.historyDraft = '';
    }

    setTimeout(() => {
      const input = this.inputField?.nativeElement;
      input?.setSelectionRange(input.value.length, input.value.length);
    });
    return true;
  }

  /**
   * Navigates from assistant links, marking redirect routes so they open in the current tab.
   * @param route Route emitted by the assistant response.
   */
  navigateTo(route: string): void {
    // Assistant document links should bypass the redirect new-tab prompt and keep the user in context.
    this.router.navigateByUrl(this.getSameTabRedirectRoute(route));
    this.isOpen = false;
  }

  /**
   * Adds a reusable same-tab marker only to document redirect routes.
   * @param route Route emitted by the assistant response.
   */
  private getSameTabRedirectRoute(route: string): string {
    if (!route.startsWith('/redirect/')) return route;

    const separator = route.includes('?') ? '&' : '?';
    return `${route}${separator}redirectMode=same-tab`;
  }

  onContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const suggestion = target?.closest?.('.assistant-suggestion') as HTMLElement | null;
    if (suggestion) {
      event.preventDefault();
      const text = suggestion.getAttribute('data-suggestion') || suggestion.textContent?.replace(/[“”"]/g, '').trim();
      if (text) this.submitSuggestion(text);
      return;
    }

    const anchor = target?.closest?.('a.assistant-inline-link') as HTMLAnchorElement | null;
    if (!anchor) return;

    event.preventDefault();
    const route = anchor.getAttribute('data-route') || anchor.getAttribute('href');
    if (route) this.navigateTo(route);
  }

  /**
   * Resets visible messages, submitted-question history, and queued follow-up questions.
   */
  clearChat(): void {
    this.questionHistory = [];
    this.historyIndex = -1;
    this.historyDraft = '';
    this.pendingQuestions = [];
    this.messages = [];
    this.pushAssistant('Chat cleared. How can I help you?');
  }

  formatContent(content: string): string {
    return content
      .replace(/(?:•|â€¢)\s*"([^"]+)"/g, (_match, suggestion: string) => {
        const safeSuggestion = this.escapeHtml(suggestion);
        return `<a href="#" class="assistant-suggestion" data-suggestion="${safeSuggestion}">“${safeSuggestion}”</a>`;
      })
      .replace(/\n/g, '<br>');
  }

  /**
   * Sends a clicked help suggestion through the same queue-aware message flow.
   * @param text Suggested question text.
   */
  private submitSuggestion(text: string): void {
    this.inputText = text;
    this.sendMessage();
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, ch => {
      const replacements: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return replacements[ch];
    });
  }

  private newId(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  private pushUser(content: string): ChatMessage {
    const message = { id: this.newId(), sender: 'user' as const, content, timestamp: new Date() };
    this.messages.push(message);
    this.scrollToBottom();
    return message;
  }

  /**
   * Adds an assistant response, replacing the active typing indicator when present.
   * @param content Response text.
   * @param links Optional links rendered under the response.
   * @param isError Marks the response as an error bubble.
   * @param sourceQuestion Original user question eligible for incorrect-answer reporting.
   */
  private pushAssistant(content: string, links?: ChatLink[], isError = false, sourceQuestion?: string): void {
    const message = { id: this.newId(), sender: 'assistant' as const, content, timestamp: new Date(), links, isError, sourceQuestion: isError ? undefined : sourceQuestion };
    const loadingIndex = this.messages.findIndex(m => m.isLoading);
    if (loadingIndex >= 0) this.messages[loadingIndex] = message;
    else this.messages.push(message);
    this.scrollToBottom();
  }

  private pushLoading(): void {
    this.messages.push({ id: 'loading', sender: 'assistant', content: '', timestamp: new Date(), isLoading: true });
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messageList?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }
}

interface PendingQuestion {
  id: string;
  text: string;
  draftText: string;
  messageId: string;
  isEditing?: boolean;
}

interface VisibleTableExport {
  headers: string[];
  rows: string[][];
}
